<template>
  <!-- `ct-shell` no es decorativo: es donde corregir-test.scss declara las
       variables del producto (--ct-product y compañía) que usan todas sus
       reglas. Sin este envoltorio el corrector se renderiza sin colores. Misma
       estructura que CorrectTestsModule, el otro host del workspace. -->
  <div class="ct-shell ct-corrector-step">
    <div class="ct-direct-inner">
      <header class="ct-direct-head">
        <div>
          <div class="ct-direct-kicker">Corrección de Tests</div>
          <h2>Corrección automática de Test y obtención del informe en Word</h2>
        </div>
      </header>

      <div class="ct-direct-body">
        <CorregirTestWorkspace
          ref="workspace"
          @chat-suggestion="onChatSuggestion"
          @report-downloaded="onReportDownloaded"
        />
      </div>
    </div>

    <!-- La guía que el corrector va dictando ("ya corregiste X, ¿agregamos
         otro?"). El workspace la emite; acá se muestra en una burbuja del
         producto en vez de perderse. -->
    <div v-if="suggestion" class="ct-chat-inner ct-corrector-chat">
      <div class="ct-chat-intro">
        <div class="ct-chat-row">
          <div ref="chatBubble" class="ct-chat-bubble">
            <p>{{ suggestion.title }}</p>
            <span v-if="suggestion.text">{{ suggestion.text }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- El feedback se pide en un solo momento: cuando el informe ya se
         descargó bien. Montado de entrada (como estaba) se le pedía opinión a
         alguien que todavía no había corregido nada, y eso es ruido, no
         testimonio. `isLoggedIn` es el mismo guardarraíl que traía
         TestReportStep antes de fusionarse acá: la opinión anónima se guarda
         sin usuario y no sirve para citar a nadie. Nunca bloquea la descarga:
         aparece después del informe, y se puede ignorar. -->
    <div v-if="showFeedback" class="ct-corrector-feedback">
      <p class="ct-feedback-help">
        Tu informe ya está descargado. Cuéntanos qué te pareció:
      </p>
      <FeedbackComponent :code="feedbackCode" />
    </div>

    <b-row class="w-100 back-button py-2 m-1" @click="backToMenu">
      <b-col>Volver al menú</b-col>
    </b-row>
  </div>
</template>

<script>
// El corrector real (CorregirTestWorkspace) montado como un paso del chatbot.
//
// Existe como envoltorio y no se pone el workspace directo en `component:`
// porque ChatMessageComponent le pasa a todo paso custom su propio `context`
// (el del chat), y el workspace ya tiene una prop `context` con otro
// significado: el estudiante, que solo llena CorrectTestsModule dentro de la
// plataforma. Pasárselo mezclaría los dos contratos.
import CorregirTestWorkspace from "@/components/corregir-test/CorregirTestWorkspace.vue";
import FeedbackComponent from "./FeedbackComponent.vue";
import { scrollToElementDelayed } from "@/components/corregir-test/utils/guidedScroll";
import "@/components/corregir-test/styles/corregir-test.scss";

export default {
  name: "TestCorrectorStep",
  components: { CorregirTestWorkspace, FeedbackComponent },
  // Las props que ChatMessageComponent bindea en todo paso custom. No se usan
  // acá, pero declararlas evita que caigan como atributos sueltos en el DOM.
  props: {
    context: { type: Object, required: false, default: () => ({}) },
    condition: { type: String, required: false, default: "" },
    canWork: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      suggestion: null,
      reportDownloaded: false,
      // FeedbackEnum.TEST_CORRECTION en el backend.
      feedbackCode: 4,
    };
  },
  computed: {
    isLoggedIn() {
      return !!this.$store.state.login.token;
    },
    showFeedback() {
      return this.reportDownloaded && this.isLoggedIn;
    },
  },
  mounted() {
    // Sin esto ChatMessageComponent deja el botón "continuar" esperando a un
    // componente que nunca avisa que terminó de cargar.
    this.$emit("componente-listo");
  },
  methods: {
    onChatSuggestion(payload) {
      if (!payload) return;
      this.suggestion = payload;
      if (payload.scroll) {
        this.$nextTick(() =>
          scrollToElementDelayed(this.$refs.chatBubble, 72, 80)
        );
      }
    },
    onReportDownloaded() {
      this.reportDownloaded = true;
    },
    backToMenu() {
      this.$emit("go-to-step", "mainMessage");
    },
  },
};
</script>

<style scoped>
.ct-corrector-step {
  width: 100%;
}
.ct-corrector-chat {
  margin-top: 12px;
}
.ct-corrector-feedback {
  margin-top: 12px;
}
.ct-feedback-help {
  margin-bottom: 4px;
  font-weight: 600;
}
.back-button {
  border-radius: 5px;
  border: 2pt solid rgb(0, 0, 0) !important;
  font-family: "Ubuntu Medium", Arial, sans-serif;
  font-weight: bold;
  cursor: pointer;
  text-align: center;
}
</style>
